import moment from 'moment'
import {$} from '@/facade'
import {InputType} from '@simpli/vue-input/lib/InputType'
import {Lang} from '@/enums/Lang'

export abstract class FilterHelper {
  static toString(input?: InputType) {
    return input !== null && input !== undefined ? String(input) : ''
  }

  static bool(input?: InputType | boolean) {
    return input !== undefined && input !== null
      ? $.translate(`boolean.${Boolean(input)}`)
      : ''
  }

  static datetime(input?: moment.MomentInput | null) {
    const date = moment(input)
    return date.isValid() ? date.format($.translate('dateFormat.datetime')) : ''
  }

  static date(input?: moment.MomentInput | null) {
    const date = moment(input)
    return date.isValid() ? date.format($.translate('dateFormat.date')) : ''
  }

  static time(input?: moment.MomentInput | null) {
    const date = moment(input)
    return date.isValid() ? date.format($.translate('dateFormat.time')) : ''
  }

  static truncate(input?: InputType, length?: number) {
    const value = this.toString(input)
    if (value.length > (length || 0)) {
      return `${value.substring(0, length)}...`
    }
    return value
  }

  static stripHtml(input?: InputType) {
    const value = this.toString(input)
    const doc = new DOMParser().parseFromString(value, 'text/html')
    return doc.body.textContent || ''
  }

  static removeDelimiters(input?: InputType) {
    return this.toString(input).replace(/[. ,:\-/]+/g, '')
  }

  static phone(input?: InputType) {
    return this.toString(input)
      .replace(/\D/g, '')
      .replace(
        new RegExp($.translate('filter.phone.regex')),
        $.translate('filter.phone.format')
      )
  }

  static zipcode(input?: InputType) {
    return this.toString(input)
      .replace(/\D/g, '')
      .replace(
        new RegExp($.translate('filter.zipcode.regex')),
        $.translate('filter.zipcode.format')
      )
  }

  static rg(input?: InputType) {
    return this.toString(input)
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d{3})(\d{3})(\d{1})$/, '$1.$2.$3-$4')
  }

  static cpf(input?: InputType) {
    return this.toString(input)
      .replace(/\D/g, '')
      .replace(/(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
  }

  static cnpj(input?: InputType) {
    return this.toString(input)
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
  }

  static cpfOrCnpj(input?: InputType) {
    const value = this.toString(input).replace(/\D/g, '')

    if (value.length === 11) {
      return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
    }

    if (value.length === 14) {
      return value.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5'
      )
    }

    return value
  }

  static pad(input?: InputType, length = 2) {
    let value = this.toString(input)
    while (value.length < length) value = `0${value}`
    return value
  }

  static decimal(
    input?: InputType,
    minimumFractionDigits = 0,
    maximumFractionDigits?: number
  ) {
    const num = Number(input)
    const language = $.app.language ?? Lang.EN_US

    try {
      if (!isNaN(num)) {
        return num.toLocaleString(language, {
          style: 'decimal',
          minimumFractionDigits,
          maximumFractionDigits,
        })
      }

      return String(input)
    } catch {
      return String(input)
    }
  }

  static secureHtml(input?: InputType) {
    return this.toString(input)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/(?:\r\n|\r|\n)/g, '<br>')
  }

  static currency(input: InputType) {
    const inputNumber = Number(input)

    if (inputNumber > 1) {
      return inputNumber.toLocaleString('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: 'currency',
        currency: 'USD',
      })
    }

    return Number(
      String(input).replace(/(\.(0*)?\d{4})\d+/g, '$1')
    ).toLocaleString('en-US', {
      style: 'currency',
      maximumFractionDigits: 10,
      currency: 'USD',
    })
  }
}
